/**
 * React Query hook for Case Stages (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { getCaseStage } from 'services/case/caseServices';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches case stages data
 * Always loads the full list by getting total count first, then fetching all items
 *
 * @param {Object} params - Query parameters
 * @param {string|Array} params.practice_area - Practice area filter (can be single ID or array)
 * @param {number} params.pageLimit - Limit for fetching stages
 * @param {string} params.pagination_token - Pagination token (default: '')
 * @param {string} params.pagination_direction - Pagination direction (default: 'forward')
 * @param {boolean} params.excludeFromLoading - Exclude from loading state (default: false)
 * @returns {Promise<Array>} - Array of case stages
 */
const fetchCaseStages = async ({
  practice_area = '',
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  excludeFromLoading = false,
}) => {
  try {
    const response = await getCaseStage({
      practice_area,
      pageLimit,
      pagination_token,
      pagination_direction,
      excludeFromLoading,
    });

    return response?.data?.case_stage || [];
  } catch (error) {
    console.error('Error fetching case stages:', error);
    throw error;
  }
};

/**
 * Hook to fetch case stages with React Query
 * Always loads the full list by getting total count first (same as useCaseStageList)
 *
 * @param {Object} options - Configuration options
 * @param {string|Array} options.practice_area - Practice area filter (default: '')
 * @param {number} options.pageLimit - Limit for fetching stages (default: uses total count)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 * @param {boolean} options.loadFullList - If true, gets total count first (default: true)
 *
 * @returns {Object} - caseStageList, isStagesLoading, error, refetch, isFetching
 */
export const useCaseStagesQuery = ({
  practice_area = '',
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  excludeFromLoading = false,
  enabled = true,
  loadFullList = true,
} = {}) => {
  // Get total count first (same as useCaseStageList) - only if loadFullList is true and pageLimit is not provided
  // This count is shared across ALL reports to ensure consistent cache keys
  const { data: countData, isLoading: isCountLoading } = useQuery({
    queryKey: ['case-stage-count'],
    queryFn: async () => {
      const countResponse = await totalCountEvent('case-stage');
      return countResponse?.data || 10;
    },
    enabled: enabled && loadFullList && !pageLimit,
    staleTime: 30 * 60 * 1000, // Increased to 30 minutes - count rarely changes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component mounts - use cache
    retry: 2,
  });

  // Determine the limit value
  // For loadFullList mode, wait for countData before proceeding
  const limit = pageLimit || (loadFullList ? countData : 10) || 10;

  // For shared caching across reports, we need to ensure all reports use the same cache key
  // Only enable the query when limit is stable (countData is loaded for loadFullList mode)
  const isLimitStable = !!pageLimit || !loadFullList || !!countData;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.caseStages.list(practice_area, limit, pagination_token, pagination_direction),
    queryFn: () =>
      fetchCaseStages({
        practice_area,
        pageLimit: limit,
        pagination_token,
        pagination_direction,
        excludeFromLoading,
      }),
    // Only fetch when enabled AND limit is stable to prevent cache key changes
    enabled: enabled && isLimitStable,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    // refetchOnMount is intentionally not set to false, allowing refetch of invalidated (stale) data
    // This ensures that when data is updated in Settings, reports see the changes immediately
    retry: 2,
  });

  return {
    caseStageList: data || [],
    isStagesLoading: isLoading || (loadFullList && isCountLoading), // Include count loading state
    isFetching,
    error,
    refetch,
  };
};
